import React, { useEffect, useState } from "react";
import { IRootState, useAppDispatch } from "../../../redux/store";
import { createSelector } from "reselect";
import StyledTable from "../../../component/table";
import DashboardFrame from "../../../component/dashboard-frame";
import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import {
  updateUserInfoThunk,
  setUserListFiltersThunk,
} from "../../../redux/thunks/user.thunk";
import { useSelector } from "react-redux";
import { ITableColumn } from "../../../component/table/component";
import { IUserFiltersDto } from "../../../interfaces/dto/user.dto";
import moment from "moment";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PersonAddDisabledOutlinedIcon from "@mui/icons-material/PersonAddDisabledOutlined";
import { IWithClassName } from "../../../theme/default.theme";
import { UserStatus } from "../../../enum/user";

const columns = [
  {
    title: "Name",
    id: "name",
    sortable: true,
  },
  {
    title: "Email",
    id: "email",
    sortable: true,
  },
  {
    title: "created At",
    id: "createdAt",
    sortable: true,
  },
  {
    title: "Status",
    id: "status",
    sortable: true,
  },
];

const selector = createSelector(
  (state: IRootState) => state.user.list,
  (userList) => ({
    isLoading: userList.isLoading,
    filters: userList.filters,
    data: (userList.data ?? []).map(
      ({ name, email, status, createdAt, id }) => ({
        name,
        email: email.address,
        status,
        createdAt,
        id,
      }),
    ),
    pagination: userList.pagination,
  }),
);

const UserList = ({ className }: IWithClassName) => {
  const { data, filters, pagination, isLoading } = useSelector(selector);
  const dispatch = useAppDispatch();
  const [userListfilters, setUserListFilters] = useState<IUserFiltersDto>({
    offset: 0,
    limit: 10,
  });

  useEffect(() => {
    dispatch(setUserListFiltersThunk(userListfilters));
  }, [dispatch, userListfilters]);

  const onPageChange = (page: number) => {
    setUserListFilters((state) => ({
      ...state,
      offset: (page - 1) * 10,
      limit: 10,
    }));
  };

  const handleStatusChange = (id: string, status: UserStatus) => {
    dispatch(updateUserInfoThunk(id, { status }));
  };

  const renderCell = (
    rowIndex: number,
    columnIndex: number,
    column: ITableColumn,
  ) => {
    const index = rowIndex - (pagination?.offset ?? 0);

    switch (column.id) {
      case "createdAt":
        return moment(
          data[index][column.id as keyof (typeof data)[0]].split(".")[0],
        ).toString();
      case "status":
        return (
          <FormControlLabel
            className="status-button-group"
            control={
              <RadioGroup
                row
                value={data[index][column.id as keyof (typeof data)[0]]}
                onChange={({ currentTarget }) =>
                  handleStatusChange(
                    data[index].id,
                    currentTarget.value as UserStatus,
                  )
                }
              >
                <Radio
                  value={UserStatus.Active}
                  icon={<PersonAddOutlinedIcon />}
                  checkedIcon={<PersonAddOutlinedIcon />}
                />
                <Radio
                  value={UserStatus.New}
                  icon={<FiberNewOutlinedIcon />}
                  checkedIcon={<FiberNewOutlinedIcon />}
                />
                <Radio
                  value={UserStatus.Deleted}
                  icon={<PersonAddDisabledOutlinedIcon />}
                  checkedIcon={<PersonAddDisabledOutlinedIcon />}
                />
              </RadioGroup>
            }
            label={<></>}
          />
        );
      default:
        return data[index][column.id as keyof (typeof data)[0]];
    }
  };

  const onColumnOrder = (
    columnIndex: number,
    column: ITableColumn,
    direction: "asc" | "desc",
  ) => {
    setUserListFilters((state) => ({
      ...state,
      sortBy: column.id,
      sortDirection: direction,
    }));
  };

  return (
    <DashboardFrame title="Profile">
      <Box className={className}>
        <StyledTable
          columns={columns}
          currentPage={pagination?.page ?? 1}
          renderCell={renderCell}
          rowsPerPage={filters?.limit ?? 10}
          totalRows={pagination?.total ?? 0}
          uniqueRowId={(rowIndex) => `${rowIndex}`}
          customHeader="User List"
          onPageChange={onPageChange}
          onColumnOrder={onColumnOrder}
          isLoading={isLoading}
        />
      </Box>
    </DashboardFrame>
  );
};

export default UserList;
